import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Volume2, Pause, Home, Star } from 'lucide-react';
import { GameStage } from '../components/GameStage';
import { Piko, POSES } from '../components/Piko';
import { ClayButton } from '../components/ClayButton';
import { ActivityHost } from '../games/ActivityHost';
import { audio } from '../core/audio';
import { getState, addStars, recordSession, updateProgress, earnSticker, hasTriedGame, markGameTried } from '../core/storage';
import { getLevel, recordGameResult, isFreeJourneyComplete, replayCandidate, starsForPlay } from '../core/adaptive';
import { detectMilestones } from '../core/milestones';
import { t, lang } from '../core/i18n';
import { WORLDS, GAMES, getGame, gameName, gameBg, canPlayGame, proGameCount, FREE_GAME_IDS, nextPlayableGame } from '../content/games';
import { startSession, countSessionGame, sessionHasBudget, endSession, upsellShownThisSession, markUpsellShown } from '../core/session';
import { STICKERS } from '../content/collection';
import { useEntitlement } from '../core/entitlement';

const ROUNDS = 2;

function PikoCompanion({ pose, wiggle, reduced }) {
  return (
    <div className="absolute bottom-4 left-4 z-10 pointer-events-none" data-testid="piko-companion" data-pose={pose}>
      <motion.div key={wiggle} animate={reduced || wiggle === 0 ? {} : { rotate: [0, -7, 7, 0] }} transition={{ duration: 0.15 }}>
        <div className="relative w-[78px] h-[78px]">
          <AnimatePresence initial={false}>
            <motion.img key={pose} src={POSES[pose]} alt="Piko" draggable={false}
              initial={reduced ? { opacity: 1 } : { opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="absolute inset-0 w-full h-full object-cover select-none"
              style={{ borderRadius: '38%', border: '5px solid rgba(255,255,255,0.85)', boxShadow: '0 8px 20px rgba(29,53,87,0.25)', background: '#FCF9F2' }} />
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}

export default function GameSession() {
  const { gameId } = useParams();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { entitlement } = useEntitlement();
  const reduced = useReducedMotion();
  const game = getGame(gameId);
  const world = game ? WORLDS[game.world] : null;

  // All state declared BEFORE any useMemo that references it.
  const [phase, setPhase] = useState('greet');
  const [count, setCount] = useState(null);
  const [replayN, setReplayN] = useState(0);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [newSticker, setNewSticker] = useState(null);
  const [pikoPose, setPikoPose] = useState('happy');
  const [wiggle, setWiggle] = useState(0);
  const [flights, setFlights] = useState([]);
  const [displayStars, setDisplayStars] = useState(() => getState().progress.stars);
  const [pulse, setPulse] = useState(0);
  const [flash, setFlash] = useState(false);
  const results = useRef([]);
  const roundStart = useRef(null);
  const counterRef = useRef(null);

  const isTrial = params.get('trial') === '1' && !!game && !game.free && entitlement?.plan !== 'PRO';
  const totalRounds = isTrial ? 1 : ROUNDS;

  const level = useMemo(() => (game ? getLevel(gameId) : 1), [gameId, replayN]); // eslint-disable-line
  const rounds = useMemo(() => {
    if (!game) return [];
    return Array.from({ length: totalRounds }, (_, i) => ({ ...game.gen(level), skill: game.skill, id: `${gameId}-r${i}` }));
  }, [gameId, level, replayN, totalRounds]); // eslint-disable-line

  useEffect(() => { if (phase === 'round') roundStart.current = Date.now(); }, [phase, idx]);

  useEffect(() => {
    if (phase === 'greet') setPikoPose('happy');
    else if (phase === 'round') setPikoPose('think');
  }, [phase]);

  useEffect(() => {
    const onAnswer = (e) => {
      if (e.detail?.correct) {
        setPikoPose('celebrate');
        const tm = setTimeout(() => setPikoPose('think'), 900);
        return () => clearTimeout(tm);
      }
      setWiggle((w) => w + 1);
    };
    window.addEventListener('pp-answer', onAnswer);
    return () => window.removeEventListener('pp-answer', onAnswer);
  }, []);

  useEffect(() => {
    const open = game && canPlayGame(entitlement, gameId, getState().progress.stars);
    if (!game || (!open && !isTrial)) { nav('/home'); return; }
    if (isTrial && !open) {
      if (hasTriedGame(gameId) && !sessionStorage.getItem(`pp_trial_live_${gameId}`)) { nav(`/world/${game.world}`); return; }
      sessionStorage.setItem(`pp_trial_live_${gameId}`, '1');
      markGameTried(gameId);
    }
    if (!isTrial) startSession();
    audio.init();
    audio.music(world.music);
    audio.playVoice('countdown');
    const seq = [3, 2, 1, 'JOM'];
    const timers = seq.map((v, i) => setTimeout(() => setCount(v), 350 + i * 850));
    timers.push(setTimeout(() => setPhase('round'), 350 + seq.length * 850 + 350));
    return () => { timers.forEach(clearTimeout); audio.stopVoice(); };
  }, [replayN]); // eslint-disable-line

  useEffect(() => () => { try { sessionStorage.removeItem(`pp_trial_live_${gameId}`); } catch {} }, [gameId]);

  const stars = results.current.filter((x) => x.correct).length;

  useEffect(() => {
    if (phase !== 'reward' && phase !== 'trialEnd') return;
    const n = Math.min(3, Math.max(1, stars));
    if (reduced) {
      setDisplayStars(getState().progress.stars);
      setFlash(true);
      const tm = setTimeout(() => setFlash(false), 350);
      return () => clearTimeout(tm);
    }
    setFlights(Array.from({ length: n }, (_, i) => i));
    const tm = setTimeout(() => {
      setFlights([]);
      setDisplayStars(getState().progress.stars);
      setPulse((p) => p + 1);
    }, 650 + n * 120);
    return () => clearTimeout(tm);
  }, [phase]); // eslint-disable-line

  // A brand new sticker used to require tapping "OK!" before anything could
  // continue, which broke the no-tap flow on the very first play of every
  // game — exactly when it matters most. Show it, then move on by itself.
  useEffect(() => {
    if (phase !== 'sticker') return;
    const tm = setTimeout(() => { audio.playSfx('star'); setPhase('reward'); }, 2600);
    return () => clearTimeout(tm);
  }, [phase]);

  // Auto-advance: after the reward beat, flow into the next game with no tap.
  // Bounded by the parent's session length — a 2-year-old cannot self-regulate
  // a stop, so the session must end itself rather than loop forever.
  useEffect(() => {
    if (phase !== 'reward' || isTrial) return;
    const nextId = nextPlayableGame(gameId, entitlement, getState().progress.stars);
    const budget = sessionHasBudget();
    // Out of new games: take them straight to the locked worlds so the parent
    // sees what is waiting. That screen shows worlds and objectives only — the
    // price still sits behind the parent gate — so there is no reason to
    // withhold it. Once per session, then loop back into finished games.
    const boundaryReady = budget && !nextId && isFreeJourneyComplete(FREE_GAME_IDS) && !upsellShownThisSession();
    const loopBackId = budget && !nextId && !boundaryReady ? replayCandidate(FREE_GAME_IDS) : null;
    const tm = setTimeout(() => {
      if (budget && nextId) {
        audio.playSfx('transition');
        nav(`/game/${nextId}`);
      } else if (boundaryReady) {
        markUpsellShown();
        endSession();
        audio.stopMusic();
        nav('/next-adventures');
      } else if (loopBackId && loopBackId !== gameId) {
        audio.playSfx('transition');
        nav(`/game/${loopBackId}`);
      } else {
        updateProgress({ fed: getState().progress.fed + 1 });
        audio.playSfx('reward');
        audio.playVoice('feed_piko');
        setPhase('feed');
      }
    }, 2600);
    return () => clearTimeout(tm);
  }, [phase]); // eslint-disable-line

  // Feeding Piko is the session's closing ritual — it finishes on its own.
  useEffect(() => {
    if (phase !== 'feed') return;
    const tm = setTimeout(() => {
      audio.music('theme_calm');
      audio.playVoice('goodbye');
      setPhase('goodbye');
    }, 2200);
    return () => clearTimeout(tm);
  }, [phase]); // eslint-disable-line

  useEffect(() => { if (phase === 'goodbye') endSession(); }, [phase]);

  if (!game || !world) return null;
  const en = lang() === 'en';
  const proCount = proGameCount();

  const onRoundDone = (r) => {
    const durationMs = roundStart.current ? Date.now() - roundStart.current : null;
    results.current = [...results.current, { ...r, durationMs }];
    if (idx + 1 < rounds.length) { audio.playSfx('transition'); setIdx(idx + 1); return; }
    const total = results.current.reduce((a, x) => ({ attempts: a.attempts + x.attempts, hints: a.hints + x.hints }), { attempts: 0, hints: 0 });
    const earnedNow = results.current.filter((x) => x.correct).length;
    addStars(earnedNow);
    recordSession({ date: new Date().toISOString(), world: game.world, game: gameId, level, stars: earnedNow,
      activities: results.current.map((x, i) => ({ id: `${gameId}-r${i}`, ...x, skill: game.skill })) });
    if (isTrial) {
      audio.playSfx('star');
      audio.playVoice('star');
      setPhase('trialEnd');
      return;
    }
    recordGameResult(gameId, { ...total, correct: true, stars: starsForPlay({ rounds: rounds.length, attempts: total.attempts, hints: total.hints, stars: earnedNow }) });
    // After recordGameResult, so a world that just completed is already marked.
    detectMilestones({ gameId, worldId: game.world, skill: game.skill, rounds: rounds.length, attempts: total.attempts });
    countSessionGame();
    // A game with no sticker defined must still finish. Without this guard the
    // sticker phase renders nothing and the child is stranded on a blank screen.
    const got = earnSticker(gameId) && !!STICKERS[gameId];
    if (got) {
      setNewSticker(STICKERS[gameId]);
      audio.playSfx('reward');
      audio.playVoice('sticker');
      setPhase('sticker');
    } else {
      audio.playSfx('star');
      audio.playVoice('star');
      setPhase('reward');
    }
  };

  const goodbye = () => {
    audio.music('theme_calm');
    audio.playVoice('goodbye');
    setPhase('goodbye');
  };

  const exitToMap = () => {
    audio.stopMusic();
    if (isFreeJourneyComplete(FREE_GAME_IDS) && !getState().freeJourneyDone) nav('/next-adventures');
    else nav(`/world/${game.world}`);
  };

  const replay = () => {
    audio.playSfx('pickup');
    results.current = [];
    setIdx(0); setNewSticker(null); setCount(null); setPhase('greet');
    setReplayN((n) => n + 1);
  };

  return (
    <GameStage bg={gameBg(game)} testId={`game-${gameId}`}>
      <div className="flex items-center justify-between px-4 pt-4 z-10">
        <button data-testid="session-exit-btn" onClick={() => { audio.stopMusic(); nav(`/world/${game.world}`); }}
          className="w-14 h-14 rounded-full bg-white/85 backdrop-blur flex items-center justify-center shadow">
          <Home size={26} strokeWidth={3} className="text-navy/70" />
        </button>
        <div className="flex items-center gap-2 bg-white/80 backdrop-blur rounded-full px-4 h-11 shadow" data-testid="level-indicator">
          {[1, 2, 3].map((l) => <div key={l} className={`w-3 h-3 rounded-full ${l <= level ? 'bg-sunshine' : 'bg-navy/15'}`} />)}
          <span className="w-px h-5 bg-navy/10 mx-1" />
          <span ref={counterRef} className="flex items-center gap-1" data-testid="session-star-count">
            <Star size={18} className="text-sunshine fill-sunshine" />
            <motion.span key={pulse} animate={pulse > 0 && !reduced ? { scale: [1, 1.35, 1] } : {}} transition={{ duration: 0.3 }}
              className={`font-heading font-bold transition-colors duration-300 ${flash ? 'text-coral' : 'text-navy'}`}>{displayStars}</motion.span>
          </span>
        </div>
        <div className="flex gap-3">
          {phase === 'round' && (
            <button data-testid="voice-replay-btn" onClick={() => audio.playVoice(rounds[idx]?.voice, rounds[idx]?.voiceAlt)}
              className="w-14 h-14 rounded-full bg-white/85 backdrop-blur flex items-center justify-center shadow">
              <Volume2 size={26} strokeWidth={3} className="text-sky" />
            </button>
          )}
          <button data-testid="session-pause-btn" onClick={() => setPaused(true)}
            className="w-14 h-14 rounded-full bg-white/85 backdrop-blur flex items-center justify-center shadow">
            <Pause size={26} strokeWidth={3} className="text-navy/70" />
          </button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {phase === 'greet' && (
          <motion.div key="greet" exit={{ opacity: 0, scale: 1.05 }} className="flex-1 flex flex-col items-center justify-center gap-5 px-6" data-testid="countdown-overlay">
            <h2 className="font-heading text-2xl font-bold text-white text-center drop-shadow-lg">{gameName(game, en)}</h2>
            <div className="h-32 flex items-center justify-center">
              <AnimatePresence mode="popLayout">
                {count !== null && (
                  <motion.span key={count} data-testid="countdown-number"
                    initial={reduced ? { opacity: 0 } : { scale: 0.2, opacity: 0, rotate: -12 }}
                    animate={reduced ? { opacity: 1 } : { scale: 1, opacity: 1, rotate: 0 }}
                    exit={reduced ? { opacity: 0 } : { scale: 1.6, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 320, damping: 16 }}
                    className="font-heading font-bold text-white drop-shadow-2xl"
                    style={{ fontSize: count === 'JOM' ? '4.5rem' : '6.5rem', WebkitTextStroke: '3px rgba(29,53,87,0.35)' }}>
                    {count === 'JOM' ? 'Jom!' : count}
                  </motion.span>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
        {phase === 'round' && !paused && (
          <motion.div key={`r-${idx}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex-1 flex flex-col">
            <ActivityHost activity={rounds[idx]} onComplete={onRoundDone} />
          </motion.div>
        )}
        {phase === 'sticker' && newSticker && (
          <motion.div key="sticker" initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} className="flex-1 flex flex-col items-center justify-center gap-6 px-6">
            <motion.div initial={{ rotate: -10, scale: 0 }} animate={{ rotate: 0, scale: 1 }} transition={{ type: 'spring', delay: 0.2 }}
              className="w-40 h-40 rounded-[2.5rem] bg-white/95 flex items-center justify-center text-7xl shadow-2xl" style={{ border: '6px solid #FFD166' }} data-testid="new-sticker">
              {newSticker.emoji}
            </motion.div>
            <p className="font-heading text-2xl font-bold text-white drop-shadow-md">{newSticker.name[en ? 1 : 0]}</p>
            <ClayButton small testId="sticker-continue-btn" onClick={() => { audio.playSfx('star'); setPhase('reward'); }}>OK!</ClayButton>
          </motion.div>
        )}
        {phase === 'reward' && (
          <motion.div key="reward" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="flex-1 flex flex-col items-center justify-center gap-6 px-6">
            <div className="flex gap-2" data-testid="reward-stars">
              {Array.from({ length: Math.max(1, stars) }).map((_, i) => (
                <motion.div key={i} initial={{ scale: 0, rotate: -30 }} animate={{ scale: 1, rotate: 0 }} transition={{ delay: 0.3 + i * 0.25, type: 'spring' }}>
                  <Star size={56} className="text-sunshine fill-sunshine drop-shadow-lg" />
                </motion.div>
              ))}
            </div>
            <Piko pose="celebrate" size={150} />
            <ClayButton testId="feed-piko-btn" onClick={() => { updateProgress({ fed: getState().progress.fed + 1 }); audio.playSfx('reward'); audio.playVoice('feed_piko'); setPhase('feed'); }}>
              {t('feedPiko')}
            </ClayButton>
          </motion.div>
        )}
        {phase === 'trialEnd' && (
          <motion.div key="trialEnd" initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} className="flex-1 flex flex-col items-center justify-center gap-5 px-6" data-testid="trial-end">
            <div className="flex gap-2" data-testid="reward-stars">
              {Array.from({ length: Math.max(1, stars) }).map((_, i) => (
                <motion.div key={i} initial={{ scale: 0, rotate: -30 }} animate={{ scale: 1, rotate: 0 }} transition={{ delay: 0.3 + i * 0.25, type: 'spring' }}>
                  <Star size={50} className="text-sunshine fill-sunshine drop-shadow-lg" />
                </motion.div>
              ))}
            </div>
            <Piko pose="point" size={140} float={false} />
            <p className="font-heading text-xl font-bold text-white text-center drop-shadow-md bg-navy/30 backdrop-blur rounded-2xl px-5 py-3" data-testid="trial-end-message">
              {t('trialEndMsg').replace('{m}', proCount)}
            </p>
            <ClayButton testId="trial-upgrade-btn" onClick={() => { audio.stopMusic(); nav('/parent-gate?next=upgrade'); }}>{t('parentArea')}</ClayButton>
            <button data-testid="trial-back-btn" onClick={exitToMap} className="font-body font-bold text-white/90 underline min-h-[56px] min-w-[56px] px-4">{t('backHome')}</button>
          </motion.div>
        )}
        {phase === 'feed' && (
          <motion.div key="feed" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex-1 flex flex-col items-center justify-center gap-6">
            <Piko pose="eat" size={180} />
            <ClayButton small testId="feed-done-btn" color="#4ADE80" onClick={goodbye}>{t('done')}</ClayButton>
          </motion.div>
        )}
        {phase === 'goodbye' && (
          <motion.div key="bye" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }} className="flex-1 flex flex-col items-center justify-center gap-4 px-8">
            <Piko pose="sleep" size={140} float={false} />
            <p className="font-heading text-2xl font-bold text-white text-center drop-shadow-md bg-navy/30 backdrop-blur rounded-2xl px-5 py-3" data-testid="goodbye-message">{t('goodbye')}</p>
            <div className="flex items-center gap-1.5 bg-white/85 rounded-full px-4 py-2 shadow" data-testid="goodbye-stars">
              <Star size={20} className="text-sunshine fill-sunshine" />
              <span className="font-heading font-bold text-navy">+{stars}</span>
            </div>
            <p className="font-body text-sm font-bold text-white/90 drop-shadow text-center" data-testid="play-again-hint">{t('playAgainHint')}</p>
            <div className="flex gap-3">
              <ClayButton small testId="replay-btn" color="#4ADE80" onClick={replay}>{t('playAgain')}</ClayButton>
              <ClayButton small testId="goodbye-home-btn" onClick={exitToMap}>{t('backHome')}</ClayButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {(phase === 'greet' || (phase === 'round' && !paused)) && (
        <PikoCompanion pose={pikoPose} wiggle={wiggle} reduced={reduced} />
      )}

      {flights.length > 0 && (
        <div className="fixed inset-0 z-30 pointer-events-none">
          {flights.map((i) => {
            const rect = counterRef.current?.getBoundingClientRect();
            const dx = rect ? rect.left + rect.width / 2 - window.innerWidth / 2 : 0;
            const dy = rect ? rect.top + rect.height / 2 - window.innerHeight / 2 : -300;
            return (
              <motion.div key={i} data-testid="flying-star" className="absolute left-1/2 top-1/2"
                initial={{ x: -24, y: -24, scale: 1.2, opacity: 1 }}
                animate={{ x: dx - 24, y: dy - 24, scale: 0.45, opacity: 0.9 }}
                transition={{ duration: 0.6, delay: i * 0.12, ease: 'easeIn' }}>
                <Star size={48} className="text-sunshine fill-sunshine drop-shadow-lg" />
              </motion.div>
            );
          })}
        </div>
      )}

      {paused && (
        <div className="absolute inset-0 bg-navy/50 backdrop-blur-md flex flex-col items-center justify-center gap-6 z-20" data-testid="pause-overlay">
          <Piko pose="sleep" size={130} float={false} />
          <p className="font-heading text-2xl font-bold text-white">{t('pause')}</p>
          <ClayButton small testId="resume-btn" onClick={() => setPaused(false)}>{t('resume')}</ClayButton>
          <button data-testid="pause-exit-btn" onClick={() => { audio.stopMusic(); nav(`/world/${game.world}`); }} className="font-body text-white/90 underline min-h-[56px] min-w-[56px]">{t('exit')}</button>
        </div>
      )}
    </GameStage>
  );
}
